import { FC, useState, useRef, useEffect, useMemo } from 'react';
import { atom, useAtom, useSetAtom, useAtomValue } from 'jotai';
import { FilterableMultiSelect } from '@carbon/react';
import {
  CountyCoverageFacets,
  StateFacet,
  CountyFacet,
} from 'types/api-responses';
import styles from './state-county-filters.module.scss';

interface StateCountyFiltersProps {
  facets: CountyCoverageFacets | undefined;
  selectedStateIds: number[];
  selectedCountyIds: number[];
  onStateIdsChange: (stateIds: number[]) => void;
  onCountyIdsChange: (countyIds: number[]) => void;
}

const facetsCacheAtom = atom<{
  states: Map<number, StateFacet>;
  counties: Map<number, CountyFacet>;
}>({
  states: new Map(),
  counties: new Map(),
});

export const StateCountyFilters: FC<StateCountyFiltersProps> = ({
  facets,
  selectedStateIds,
  selectedCountyIds,
  onStateIdsChange,
  onCountyIdsChange,
}) => {
  const [stateFilterOpen, setStateFilterOpen] = useState(false);
  const [countyFilterOpen, setCountyFilterOpen] = useState(false);
  const [stateResetKey, setStateResetKey] = useState(0);
  const [countyResetKey, setCountyResetKey] = useState(0);

  const stateSelectionInProgress = useRef(false);
  const countySelectionInProgress = useRef(false);

  const prevStateIdsAtom = useMemo(
    () => atom<number[]>(selectedStateIds),
    [selectedStateIds]
  );
  const prevCountyIdsAtom = useMemo(
    () => atom<number[]>(selectedCountyIds),
    [selectedCountyIds]
  );

  const [facetsCache, setFacetsCache] = useAtom(facetsCacheAtom);

  const setPrevStateIds = useSetAtom(prevStateIdsAtom);
  const setPrevCountyIds = useSetAtom(prevCountyIdsAtom);

  const prevStateIds = useAtomValue(prevStateIdsAtom);
  const prevCountyIds = useAtomValue(prevCountyIdsAtom);

  useEffect(() => {
    if (facets) {
      setFacetsCache((prev) => {
        const newStates = new Map(prev.states);
        const newCounties = new Map(prev.counties);

        if (facets.states) {
          facets.states.forEach((state) => {
            newStates.set(state.id, state);
          });
        }

        if (facets.counties) {
          facets.counties.forEach((county) => {
            newCounties.set(county.id, county);
          });
        }

        return {
          states: newStates,
          counties: newCounties,
        };
      });
    }
  }, [facets, setFacetsCache]);

  useEffect(() => {
    const wasCleared = prevStateIds.length > 0 && selectedStateIds.length === 0;
    const wasExternalChange =
      !stateSelectionInProgress.current && prevStateIds !== selectedStateIds;

    if (
      wasCleared ||
      (wasExternalChange && selectedStateIds.length < prevStateIds.length)
    ) {
      setStateResetKey((k) => k + 1);
    }
    setPrevStateIds(selectedStateIds);
  }, [selectedStateIds, prevStateIds, setPrevStateIds]);

  useEffect(() => {
    const wasCleared =
      prevCountyIds.length > 0 && selectedCountyIds.length === 0;
    const wasExternalChange =
      !countySelectionInProgress.current && prevCountyIds !== selectedCountyIds;

    if (
      wasCleared ||
      (wasExternalChange && selectedCountyIds.length < prevCountyIds.length)
    ) {
      setCountyResetKey((k) => k + 1);
    }
    setPrevCountyIds(selectedCountyIds);
  }, [selectedCountyIds, prevCountyIds, setPrevCountyIds]);

  const stateItems = useMemo(() => {
    const currentStateItems =
      facets?.states.map((state) => ({
        id: state.id.toString(),
        label: state.name,
        name: state.name,
      })) || [];

    const cachedStateItems: Array<{ id: string; label: string; name: string }> =
      [];
    selectedStateIds.forEach((stateId) => {
      const isInCurrentFacets = facets?.states.some((s) => s.id === stateId);
      if (!isInCurrentFacets) {
        const cachedState = facetsCache.states.get(stateId);
        if (cachedState) {
          cachedStateItems.push({
            id: cachedState.id.toString(),
            label: cachedState.name,
            name: cachedState.name,
          });
        }
      }
    });

    const allStateItems = [...currentStateItems];
    cachedStateItems.forEach((cachedItem) => {
      if (!allStateItems.some((item) => item.id === cachedItem.id)) {
        allStateItems.push(cachedItem);
      }
    });

    return allStateItems;
  }, [facets?.states, selectedStateIds, facetsCache.states]);

  const countyItems = useMemo(() => {
    const currentCountyItems =
      facets?.counties.map((county) => ({
        id: county.id.toString(),
        label: `${county.countyShortName}, ${county.stateAbbreviation}`,
        name: `${county.countyName}, ${county.stateAbbreviation}`,
      })) || [];

    const cachedCountyItems: Array<{
      id: string;
      label: string;
      name: string;
    }> = [];
    selectedCountyIds.forEach((countyId) => {
      const isInCurrentFacets = facets?.counties.some((c) => c.id === countyId);
      if (!isInCurrentFacets) {
        const cachedCounty = facetsCache.counties.get(countyId);
        if (cachedCounty) {
          cachedCountyItems.push({
            id: cachedCounty.id.toString(),
            label: `${cachedCounty.countyShortName}, ${cachedCounty.stateAbbreviation}`,
            name: `${cachedCounty.countyName}, ${cachedCounty.stateAbbreviation}`,
          });
        }
      }
    });

    const allCountyItems = [...currentCountyItems];
    cachedCountyItems.forEach((cachedItem) => {
      if (!allCountyItems.some((item) => item.id === cachedItem.id)) {
        allCountyItems.push(cachedItem);
      }
    });

    return allCountyItems;
  }, [facets?.counties, selectedCountyIds, facetsCache.counties]);

  const selectedStateItems = stateItems.filter((item) =>
    selectedStateIds.includes(Number(item.id))
  );

  const selectedCountyItems = countyItems.filter((item) =>
    selectedCountyIds.includes(Number(item.id))
  );

  const handleStateChange = (event: {
    selectedItems: Array<{ id: string }>;
  }) => {
    stateSelectionInProgress.current = true;
    const ids = event.selectedItems.map((item) => Number(item.id));
    onStateIdsChange(ids);
    setTimeout(() => {
      stateSelectionInProgress.current = false;
    }, 0);
  };

  const handleCountyChange = (event: {
    selectedItems: Array<{ id: string }>;
  }) => {
    countySelectionInProgress.current = true;
    const ids = event.selectedItems.map((item) => Number(item.id));
    onCountyIdsChange(ids);
    setTimeout(() => {
      countySelectionInProgress.current = false;
    }, 0);
  };

  const handleStateMenuChange = (isOpen: boolean) => {
    if (!isOpen && stateSelectionInProgress.current) {
      setStateFilterOpen(true);
    } else {
      setStateFilterOpen(isOpen);
    }
  };

  const handleCountyMenuChange = (isOpen: boolean) => {
    if (!isOpen && countySelectionInProgress.current) {
      setCountyFilterOpen(true);
    } else {
      setCountyFilterOpen(isOpen);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.filterGroup}>
        <FilterableMultiSelect
          key={`state-filter-${stateItems.length}-${stateResetKey}`}
          id="state-filter"
          titleText="States"
          placeholder="Select states"
          items={stateItems}
          itemToString={(item: any) => (item ? item.label : '')}
          initialSelectedItems={selectedStateItems}
          onChange={handleStateChange}
          open={stateFilterOpen}
          onMenuChange={handleStateMenuChange}
          selectionFeedback="fixed"
        />
      </div>
      <div className={styles.filterGroup}>
        <FilterableMultiSelect
          key={`county-filter-${countyItems.length}-${countyResetKey}`}
          id="county-filter"
          titleText="Counties"
          placeholder="Select counties"
          items={countyItems}
          itemToString={(item: any) => (item ? item.label : '')}
          initialSelectedItems={selectedCountyItems}
          onChange={handleCountyChange}
          open={countyFilterOpen}
          onMenuChange={handleCountyMenuChange}
          disabled={countyItems.length === 0}
          selectionFeedback="fixed"
        />
      </div>
    </div>
  );
};
